import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useCallback } from 'react';

import { useFeature } from '~/modules/root/hook/use-feature';
import type { EnquiryFormConfig } from '~/modules/enquiry/types/public-enquiry-form';
import type { RoutedDestination } from '~/modules/enquiry/domain/enquiry-outcome';
import { discardStaleGateOpen } from '~/modules-rocco/user/auth-gate/discard-stale-gate-open';
import type {
    BookingContact,
    BookingFormAnswers,
    BookingSlot,
} from './domain/book-meeting-flow';

export interface BookMeetingGateIntent {
    /** Professional the meeting is with, and who receives the request. */
    professionalUrlSegment: string;
    /** Page the request is about: a project, a profile, a product. */
    siteTreeId: number;
    /** The professional's id, which a {@link bookingFormId} form submits to. */
    professionalId?: number;
    /** Named in the enquiry body, e.g. a project title. */
    itemTitle?: string;
    /**
     * Bookable Client Director sessions, when the professional has a connected
     * calendar. Empty means the working-week request flow instead.
     */
    sessionIds?: number[];
    /**
     * Team member to open on, for a CTA that names a person (the Our Team
     * cards). Deliberately the TeamMember row id, not the member account id:
     * the row id is already on the page that renders the tile, whereas the
     * account id is not, and there is no reason for a profile page to carry
     * account identifiers for its staff. The gate maps one to the other from
     * the availability it loads anyway.
     */
    preferredTeamMemberId?: number;
    /**
     * That person's name, as the page already shows it.
     *
     * Sent with the id so the pane can name them on the first frame. The id
     * alone means the pane has to wait for the roster to say who it belongs to,
     * and until then it can only fall back to the practice: the reader clicked
     * a face and watched the panel say "Meet with <the company>" before
     * correcting itself.
     */
    preferredTeamMemberName?: string;
    /**
     * That person's own booking form (Business Manager Forms). The gate asks
     * its questions first, then books only them: the enquiry the form sends is
     * what the booking or request is attached to.
     */
    bookingFormId?: number;
    /**
     * The page's own Book a meeting form (Business Manager Forms), whose
     * questions and routing run before a time is offered. A personal
     * {@link bookingFormId} takes precedence over it.
     */
    formId?: number;
    /** That form's resolved configuration, prefetched by the page's loader. */
    formConfig?: EnquiryFormConfig;
    /** The form asks questions or routes, so its details step comes first. */
    detailsFirst?: boolean;
    /** The Forms page type the button sits on. */
    pageType?: 'Product' | 'Professional' | 'Project' | 'Article';
    /**
     * The words on the page's Book button, so the modal is headed the way the reader
     * asked ("Request a Meeting"). Left out where the opener has its own words.
     */
    ctaLabel?: string;
    /** Face or logo for the modal's left pane, so it matches the CTA. */
    paneImage?: string;
    /**
     * The page knows the practice shows more than one person, so the loading
     * state can reserve the picker rather than let it drop in.
     *
     * Only set where the count is already on the page. Guessing it is worse
     * than leaving it out: most professionals have no public team member at
     * all, and a reserved field that then collapses moves the calendar 59px.
     */
    expectsTeamPicker?: boolean;
    /**
     * The practice, as the page already knows it, so the left pane can name and
     * badge them on the first frame instead of holding the ArchiPro brand pane
     * up for the length of the availability fetch.
     *
     * Only the practice, never the person: whether a given face is bookable is
     * decided by the payload being fetched, so naming one here would risk
     * showing a name that the answer then contradicts. The person's name lands
     * when it is known to be true. See BC-5174.
     */
    professionalName?: string;
    companyLogo?: string | null;
    logoBackgroundColor?: string | null;
}

/**
 * What the reader was trying to book before the gate stopped them, persisted so
 * it survives a social round-trip's full page reload. Session-scoped: an
 * abandoned intent must not follow them into a later visit and open a booking
 * they have forgotten asking for.
 */
export const BOOK_MEETING_GATE_INTENT_KEY = 'book-meeting-gate-intent';
export const BOOK_MEETING_GATE_OPEN_KEY = 'book-meeting-gate-open';

export const bookMeetingGateIntentAtom =
    atomWithStorage<BookMeetingGateIntent | null>(
        BOOK_MEETING_GATE_INTENT_KEY,
        null,
        createJSONStorage<BookMeetingGateIntent | null>(() => sessionStorage),
    );

/** Names this gate on every funnel event and on the params a social redirect appends. */
export const BOOK_MEETING_AUTH_SOURCE = 'bookMeeting' as const;

/**
 * How far the reader got with the intent: their answers, the time and the
 * contact details they gave, and what the server has already done with them.
 * Persisted beside the intent so a social sign-in, which reloads the page,
 * lands back on a send that is ready to go rather than an empty form.
 */
export interface BookMeetingGateProgress {
    answers: BookingFormAnswers | null;
    /** The form's questions were answered and passed. */
    detailsDone: boolean;
    slot: BookingSlot | null;
    contact: BookingContact | null;
    /**
     * The enquiry already created for this booking. Never created twice: a
     * resume after sign-in, or a re-pick of the time, reuses it.
     */
    enquiryId: number | null;
    /** Where the server routed that enquiry. */
    routed: RoutedDestination | null;
    /** The reader pressed send; the gate finishes it once they are signed in. */
    sending: boolean;
}

export const BOOK_MEETING_GATE_PROGRESS_KEY = 'book-meeting-gate-progress';

export const EMPTY_BOOK_MEETING_PROGRESS: BookMeetingGateProgress = {
    answers: null,
    detailsDone: false,
    slot: null,
    contact: null,
    enquiryId: null,
    routed: null,
    sending: false,
};

// The progress holds contact details and a half-made booking. Only the social
// round-trip this gate started may carry it into a new document.
discardStaleGateOpen(
    BOOK_MEETING_GATE_OPEN_KEY,
    BOOK_MEETING_GATE_PROGRESS_KEY,
    BOOK_MEETING_AUTH_SOURCE,
);

export const bookMeetingGateProgressAtom =
    atomWithStorage<BookMeetingGateProgress | null>(
        BOOK_MEETING_GATE_PROGRESS_KEY,
        null,
        createJSONStorage<BookMeetingGateProgress | null>(() => sessionStorage),
    );

/**
 * Whether the gate is showing, deliberately in memory rather than in storage.
 *
 * A persisted flag reopened the modal on the next page the reader visited, with
 * the professional they had been looking at before — so a booking for one
 * practice appeared over another's profile. The intent is still persisted, so a
 * social round-trip can pick the errand back up; only the "currently on screen"
 * part is per-page.
 */
export const bookMeetingGateOpenAtom = atom(false);

/** Read-only access for surfaces that only need to know a booking is pending. */
export const useBookMeetingGateIntent = () =>
    useAtomValue(bookMeetingGateIntentAtom);

/**
 * Opening the gate, for CTAs that render in bulk (a Book a meeting button on
 * every Our Team card) and must not re-render when the gate's state changes.
 */
export const useOpenBookMeetingGate = () => {
    const setIntent = useSetAtom(bookMeetingGateIntentAtom);
    const setOpen = useSetAtom(bookMeetingGateOpenAtom);
    const setProgress = useSetAtom(bookMeetingGateProgressAtom);

    return useCallback(
        (intent: BookMeetingGateIntent) => {
            // A new opening is a new errand: nothing chosen for the last one
            // carries over.
            setProgress(null);
            setIntent(intent);
            setOpen(true);
        },
        [setIntent, setOpen, setProgress],
    );
};

/** Full access, for the gate host itself. */
export const useBookMeetingGate = () => {
    const [intent, setIntent] = useAtom(bookMeetingGateIntentAtom);
    const [isOpen, setOpen] = useAtom(bookMeetingGateOpenAtom);
    const setProgress = useSetAtom(bookMeetingGateProgressAtom);

    const open = useCallback(
        (next: BookMeetingGateIntent) => {
            setProgress(null);
            setIntent(next);
            setOpen(true);
        },
        [setIntent, setOpen, setProgress],
    );

    /** Brings back the persisted intent and progress, after a social sign-in. */
    const reopen = useCallback(() => setOpen(true), [setOpen]);

    const close = useCallback(() => {
        setOpen(false);
        setIntent(null);
        setProgress(null);
    }, [setOpen, setIntent, setProgress]);

    return { intent, isOpen, open, reopen, close };
};

/**
 * Whether the booking gate can actually open.
 *
 * The gate finishes on the auth step that `revamp_2026` carries, and
 * {@link BookMeetingGateModal} is mounted behind the same flag so a stale
 * sessionStorage intent cannot open a gate on a build meant to have none. A
 * CTA that set the intent without it would mount nothing, so every entry point
 * reads this rather than the flag on its own.
 */
export const useBookMeetingGateEnabled = () => useFeature('revamp_2026');
